import {
	Background,
	Controls,
	type Edge,
	Handle,
	MarkerType,
	type Node,
	Position,
	ReactFlow,
	useEdgesState,
	useNodesState,
} from "@xyflow/react";
import React, { useMemo } from "react";
import "@xyflow/react/dist/style.css";
import { Avatar, Card, Flex, Tag, Typography } from "antd";
import type { Deployment } from "@/common/model";

const { Text } = Typography;

// Derive graph type directly from the SDK-generated Deployment type
type DeploymentGraph = NonNullable<Deployment["graph"]>;
type GraphNode = DeploymentGraph["nodes"][number];
type GraphRelationship = DeploymentGraph["relationships"][number];

interface ArchitectureNodeData extends Record<string, unknown> {
	id: string;
	label: string;
	serviceName: string;
	providerName?: string;
}

interface ArchitectureNode extends Node {
	data: ArchitectureNodeData;
}

interface DeploymentGraphFlowProps {
	graph: DeploymentGraph | null | undefined;
	height?: string;
}

const getGraphNodeServiceIcon = (
	providerName: string,
	serviceName: string,
): string => {
	const iconPath = `/assets/icons/${providerName.toLowerCase()}/services/${serviceName.toLowerCase()}.png`;
	return iconPath;
};

const ArchitectureNodeComponent = ({
	data,
}: {
	data: ArchitectureNodeData;
}) => {
	const [imageError, setImageError] = React.useState(false);

	const provider = data.providerName ?? "generic";

	const iconSrc = getGraphNodeServiceIcon(provider, data.serviceName);

	return (
		<>
			<Handle
				type="target"
				position={Position.Left}
				style={{
					width: 8,
					height: 8,
					left: -4,
					background: "#1864ff",
					border: "2px solid white",
				}}
			/>

			<Handle
				type="source"
				position={Position.Right}
				style={{
					width: 8,
					height: 8,
					right: -4,
					background: "#1864ff",
					border: "2px solid white",
				}}
			/>

			<Card
				size="small"
				styles={{
					body: {
						padding: 14,
					},
				}}
				style={{
					minWidth: 240,
					borderRadius: 10,
					boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
					transition: "all .2s",
					cursor: "grab",
				}}
			>
				<Flex gap={12} align="center">
					<Avatar
						size={44}
						shape="square"
						src={!imageError ? iconSrc : undefined}
						onError={() => {
							setImageError(true);
							return false;
						}}
					>
						{data.serviceName[0]}
					</Avatar>

					<Flex vertical style={{ flex: 1 }} gap={2}>
						<Text strong>{data.label}</Text>

						<Text type="secondary" style={{ fontSize: 12 }}>
							{data.serviceName}
						</Text>

						<Tag
							color="blue"
							style={{
								width: "fit-content",
								marginTop: 4,
							}}
						>
							{provider}
						</Tag>
					</Flex>
				</Flex>
			</Card>
		</>
	);
};

const nodeTypes = {
	architectureNode: ArchitectureNodeComponent,
};

function calculateLayout(
	nodes: GraphNode[],
	relationships: GraphRelationship[],
): { [id: string]: { x: number; y: number } } {
	const positions: { [id: string]: { x: number; y: number } } = {};
	const levels: { [id: string]: number } = {};

	const calculateLevel = (
		nodeId: string,
		visited = new Set<string>(),
	): number => {
		if (visited.has(nodeId)) return 0;
		if (levels[nodeId] !== undefined) return levels[nodeId];

		visited.add(nodeId);
		const dependencies = relationships.filter((r) => r.targetNodeId === nodeId);

		if (!dependencies.length) {
			levels[nodeId] = 0;
			return 0;
		}

		const maxDepLevel = Math.max(
			...dependencies.map((dep) => calculateLevel(dep.sourceNodeId, visited)),
		);
		levels[nodeId] = maxDepLevel + 1;
		return levels[nodeId];
	};

	// Populate levels for every node
	for (const node of nodes) {
		calculateLevel(node.id);
	}

	const levelGroups: { [level: number]: string[] } = {};
	Object.entries(levels).forEach(([nodeId, level]) => {
		if (!levelGroups[level]) levelGroups[level] = [];
		levelGroups[level].push(nodeId);
	});

	Object.entries(levelGroups).forEach(([level, ids]) => {
		const levelNum = parseInt(level, 10);
		const spacing = 250;
		const startY = (-(ids.length - 1) * spacing) / 2;

		ids.forEach((nodeId, index) => {
			positions[nodeId] = {
				x: levelNum * 300,
				y: startY + index * spacing,
			};
		});
	});

	return positions;
}

export function DeploymentGraphFlow({ graph }: DeploymentGraphFlowProps) {
	const { nodes, edges } = useMemo(() => {
		if (!graph?.nodes?.length) return { nodes: [], edges: [] };

		const positions = calculateLayout(graph.nodes, graph.relationships);

		const nodes: ArchitectureNode[] = graph.nodes.map((node) => ({
			id: node.id,
			type: "architectureNode",
			position: positions[node.id] ?? { x: 0, y: 0 },
			data: {
				id: node.id,
				label: node.label,
				serviceName: node.serviceName,
				providerName: node.providerName,
			},
		}));

		const edges: Edge[] = graph.relationships.map((rel) => ({
			id: rel.id,
			source: rel.sourceNodeId,
			target: rel.targetNodeId,
			type: "smoothstep",

			markerEnd: {
				type: MarkerType.ArrowClosed,
				width: 18,
				height: 18,
			},

			style: {
				stroke: "#91a3b0",
				strokeWidth: 2,
			},

			label: rel.type,

			labelStyle: {
				fontSize: 11,
				fill: "#595959",
				fontWeight: 500,
			},

			labelBgStyle: {
				fill: "#ffffff",
				fillOpacity: 0.95,
				stroke: "#d9d9d9",
				strokeWidth: 1,
				rx: 4,
			},
		}));

		return { nodes, edges };
	}, [graph]);

	// useNodesState/useEdgesState seed React state ONCE — later props are ignored, and `fitView`
	// likewise only fits on mount. Rendering a different graph into the same instance therefore
	// kept showing the first one: every deployment opened in the viewer drawer drew whichever
	// graph happened to be loaded first. Remount instead of syncing with an effect, so the
	// viewport refits to the new graph rather than staying framed on the old one's bounds.
	// lastDiscoveryAt is part of the key so a re-analysis of the SAME deployment also redraws.
	return (
		<GraphFlow
			key={`${graph?.id ?? "empty"}:${String(graph?.lastDiscoveryAt ?? "")}`}
			nodes={nodes}
			edges={edges}
		/>
	);
}

/** Renders one graph. Mounted fresh per graph by DeploymentGraphFlow — see the comment there. */
function GraphFlow({
	nodes,
	edges,
}: {
	nodes: ArchitectureNode[];
	edges: Edge[];
}) {
	const [nodesState, , onNodesChange] = useNodesState(nodes);
	const [edgesState, , onEdgesChange] = useEdgesState(edges);

	return (
		<div style={{ height: "100%", width: "100%" }}>
			<ReactFlow
				nodes={nodesState}
				edges={edgesState}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				nodeTypes={nodeTypes}
				fitView
				nodesConnectable={false}
				elementsSelectable={false}
				minZoom={0.3}
				maxZoom={1.5}
				panOnDrag
				zoomOnScroll
				nodesDraggable
			>
				<Background />
				<Controls />
			</ReactFlow>
		</div>
	);
}
